import React, { useMemo, useState, useEffect } from 'react';
import Select from 'react-select'; // Install: npm install react-select

const FiltersSection = ({
    tempFilters,
    appliedFilters,
    counts,
    loading,
    department,
    onTempFilterChange,
    onApplyFilters,
    onClearFilters,
    onRemoveFilter
}) => {
    const [courses, setCourses] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loadingCourses, setLoadingCourses] = useState(false);
    const [loadingBranches, setLoadingBranches] = useState(false);
    const genderOptions = ['Male', 'Female'];
    const placementInterestOptions = [
        { value: 'High', label: 'High' },
        { value: 'Medium', label: 'Medium' },
        { value: 'Low', label: 'Low' },
        { value: 'Critical', label: 'Critical' },
        { value: 'Non-Cooperative', label: 'Non-Cooperative' }
    ];

    // Fetch branches on mount
    useEffect(() => {
        const fetchBranches = async () => {
            setLoadingBranches(true);
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/location`,
                    {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                if (response.ok) {
                    const result = await response.json();
                    // Remove duplicates and create options
                    const uniqueBranches = [...new Set(result.map(b => b.branch))];
                    setBranches(uniqueBranches.map(branch => ({
                        value: branch,
                        label: branch
                    })));
                }
            } catch (error) {
                console.error('Error fetching branches:', error);
            } finally {
                setLoadingBranches(false);
            }
        };

        fetchBranches();
    }, []);

    // Fetch courses when department changes
    useEffect(() => {
        const fetchCourses = async () => {
            if (!department) return;

            setLoadingCourses(true);
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/course-by-department?Department=${encodeURIComponent(department)}`,
                    {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                if (response.ok) {
                    const result = await response.json();
                    if (result.success && result.courses) {
                        // Remove duplicates
                        const uniqueCourses = [...new Set(result.courses)];
                        setCourses(uniqueCourses);
                    }
                }
            } catch (error) {
                console.error('Error fetching courses:', error);
            } finally {
                setLoadingCourses(false);
            }
        };

        fetchCourses();
    }, [department]);
    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const startYear = 1995;

        const years = [];
        for (let y = currentYear; y >= startYear; y--) {
            years.push({ value: y.toString(), label: y.toString() });
        }
        return years;
    }, []);


    // Filter options
    const experienceOptions = ['Fresher', '<1 year', '1-2 years', '2-3 years', '>3 years'];
    const locationOptions = [
        'Bangalore', 'Chennai', 'Hosur', 'Trichy', 'Madurai', 'Hyderabad', 'Pune', 'Mumbai', 'Gurgaon',
        'Noida', 'Kolkata', 'Ahmedabad', 'Trivandrum', 'Coimbatore',
        'Mohali', 'Indore', 'Jaipur', 'Nagpur'
    ];

    // Remove duplicates from locations
    const uniqueLocationOptions = [...new Set(locationOptions)].map(loc => ({
        value: loc,
        label: loc
    }));

    // Course options for multi-select
    const courseOptions = courses.map(course => ({
        value: course,
        label: course
    }));

    const filterOptions = useMemo(() => [
        { value: 'all', label: `All Students ` },
        { value: 'mapped', label: `Mapped Only ` },
        { value: 'not-mapped', label: `Not Mapped ` }
    ], [counts.all, counts.mapped, counts.notMapped]);

    // Multi-select change handlers
    const handleMultiSelectChange = (name, selectedOptions) => {
        const values = selectedOptions ? selectedOptions.map(opt => opt.value).join(',') : '';
        onTempFilterChange({ target: { name, value: values } });
    };

    // Get selected values for multi-selects
    const getSelectedOptions = (filterName, options) => {
        const filterValue = tempFilters[filterName];
        if (!filterValue) return [];
        const values = filterValue.split(',');
        return options.filter(opt => values.includes(opt.value));
    };



    // Applied filters display
    const appliedFilterEntries = useMemo(() => {
        return Object.entries(appliedFilters)
            .map(([key, value]) => {
                if (!value || (key === 'mappingFilter' && value === 'all')) return null;

                let displayValue = value;
                let displayKey = key;

                switch (key) {
                    case 'mappingFilter':
                        displayKey = 'Status';
                        displayValue = value === 'mapped' ? 'Mapped Only' : value === 'not-mapped' ? 'Not Mapped' : 'All';
                        break;
                    case 'fromPassedYear':
                        displayKey = 'From Year';
                        break;
                    case 'toPassedYear':
                        displayKey = 'To Year';
                        break;
                    case 'courseName':
                        displayKey = 'Courses';
                        displayValue = value.split(',').join(', ');
                        break;
                    case 'placementInterest':
                        displayKey = 'Placement Interest';
                        displayValue = value.split(',').join(', ');
                        break;
                    case 'location':
                        displayKey = 'Locations';
                        displayValue = value.split(',').join(', ');
                        break;
                    case 'branches':
                        displayKey = 'Branches';
                        displayValue = value.split(',').join(', ');
                        break;
                    case 'studentName':
                        displayKey = 'Student Name';
                        break;
                    case 'matchScore':
                        displayKey = 'Match Score';
                        displayValue = `${value}% and above`;
                        break;
                    case 'experience':
                        displayKey = 'Experience';
                        break;
                    case 'gender':
                        displayKey = 'Gender';
                        break;
                    default:
                        displayKey = key.charAt(0).toUpperCase() + key.slice(1);
                }

                return {
                    key,
                    displayKey,
                    displayValue,
                    rawValue: value
                };
            })
            .filter(item => item !== null);
    }, [appliedFilters]);

    const hasAppliedFilters = appliedFilterEntries.length > 0;

    // Custom styles for react-select
    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '38px',
            borderRadius: '4px',
            border: '1px solid #ddd',
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#e3f2fd',
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: '#1976d2',
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: '#1976d2',
            ':hover': {
                backgroundColor: '#1976d2',
                color: 'white',
            },
        }),
    };

    return (
        <div className="filters-section">
            <div className="filters-header">
                <h3 className="section-title">Filter Matching Students</h3>
            </div>

            <div className="filters-grid">
                {/* Mapping Status */}
                <div className="filter-group">
                    <label>Mapping Status</label>
                    <select
                        name="mappingFilter"
                        value={tempFilters.mappingFilter || 'all'}
                        onChange={onTempFilterChange}
                        disabled={loading}
                    >
                        {filterOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Student Name Search */}
                <div className="filter-group">
                    <label>Student Name</label>
                    <input
                        type="text"
                        name="studentName"
                        value={tempFilters.studentName || ''}
                        onChange={onTempFilterChange}
                        placeholder="Search by student name..."
                        disabled={loading}
                    />
                </div>

                {/* Branch Multi-Select */}
                <div className="filter-group">
                    <label>Branch</label>
                    <Select
                        isMulti
                        name="branches"
                        options={branches}
                        value={getSelectedOptions('branches', branches)}
                        onChange={(selected) => handleMultiSelectChange('branches', selected)}
                        isDisabled={loading || loadingBranches}
                        placeholder="Select branches..."
                        styles={customSelectStyles}
                        isLoading={loadingBranches}
                    />
                </div>

                {/* Course Multi-Select */}
                <div className="filter-group">
                    <label>Course Name</label>
                    <Select
                        isMulti
                        name="courseName"
                        options={courseOptions}
                        value={getSelectedOptions('courseName', courseOptions)}
                        onChange={(selected) => handleMultiSelectChange('courseName', selected)}
                        isDisabled={loading || loadingCourses || !department}
                        placeholder="Select courses..."
                        styles={customSelectStyles}
                        isLoading={loadingCourses}
                    />
                    {!department && (
                        <small style={{ color: '#666', fontSize: '0.85em' }}>
                            Department required to load courses
                        </small>
                    )}
                </div>

                {/* Location Multi-Select */}
                <div className="filter-group">
                    <label>Preferred Job Location</label>
                    <Select
                        isMulti
                        name="location"
                        options={uniqueLocationOptions}
                        value={getSelectedOptions('location', uniqueLocationOptions)}
                        onChange={(selected) => handleMultiSelectChange('location', selected)}
                        isDisabled={loading}
                        placeholder="Select Preferred Job Locations..."
                        styles={customSelectStyles}
                    />
                </div>

                {/* Experience */}
                <div className="filter-group">
                    <label>Experience</label>
                    <select
                        name="experience"
                        value={tempFilters.experience || ''}
                        onChange={onTempFilterChange}
                        disabled={loading}
                    >
                        <option value="">All Experience Levels</option>
                        {experienceOptions.map(exp => (
                            <option key={exp} value={exp}>
                                {exp}
                            </option>
                        ))}
                    </select>
                </div>
                {/* Gender */}
                <div className="filter-group">
                    <label>Gender</label>
                    <select
                        name="gender"
                        value={tempFilters.gender || ''}
                        onChange={onTempFilterChange}
                        disabled={loading}
                    >
                        <option value="">All Genders</option>
                        {genderOptions.map(g => (
                            <option key={g} value={g}>{g}</option>
                        ))}
                    </select>
                </div>
                {/* From Passed Year */}
                <div className="filter-group">
                    <label>From Passed Year</label>
                    <Select
                        name="fromPassedYear"
                        options={yearOptions}
                        value={yearOptions.find(y => y.value === tempFilters.fromPassedYear) || null}
                        onChange={(selected) =>
                            onTempFilterChange({
                                target: { name: "fromPassedYear", value: selected?.value || "" }
                            })
                        }
                        isDisabled={loading}
                        placeholder="Select year"
                        styles={customSelectStyles}
                        isClearable
                    />
                </div>

                {/* To Passed Year */}
                <div className="filter-group">
                    <label>To Passed Year</label>
                    <Select
                        name="toPassedYear"
                        options={yearOptions}
                        value={yearOptions.find(y => y.value === tempFilters.toPassedYear) || null}
                        onChange={(selected) =>
                            onTempFilterChange({
                                target: { name: "toPassedYear", value: selected?.value || "" }
                            })
                        }
                        isDisabled={loading}
                        placeholder="Select year"
                        styles={customSelectStyles}
                        isClearable
                    />
                </div>
                {/* Placement Interest */}
                <div className="filter-group">
                    <label>Placement Interest</label>
                    <Select
                        isMulti
                        name="placementInterest"
                        options={placementInterestOptions}
                        value={getSelectedOptions('placementInterest', placementInterestOptions)}
                        onChange={(selected) =>
                            handleMultiSelectChange('placementInterest', selected)
                        }
                        isDisabled={loading}
                        placeholder="Select Placement Interest..."
                        styles={customSelectStyles}
                    />
                </div>
                {/* Match Score Slider */}
                <div className="filter-group">
                    <label>
                        Match Score: <strong>{tempFilters.matchScore || 0}%+</strong>
                    </label>

                    <input
                        type="range"
                        name="matchScore"
                        min="0"
                        max="100"
                        step="5"
                        value={tempFilters.matchScore || 0}
                        onChange={onTempFilterChange}
                        className="match-score-slider"
                    />

                    <div className="slider-labels">
                        <span>0%</span>
                        <span>50%</span>
                        <span>100%</span>
                    </div>
                </div>

            </div>

            {/* Filter Actions and Applied Filters */}
            <div className="filters-footer">
                {/* Applied Filters Display */}
                <div className="applied-filters-container">
                    {hasAppliedFilters && (
                        <div className="applied-filters">
                            <span className="applied-filters-label">Applied Filters:</span>
                            {appliedFilterEntries.map((filter) => (
                                <span key={filter.key} className="applied-filter-tag">
                                    {filter.displayKey}: <strong>{filter.displayValue}</strong>
                                    <button
                                        onClick={() => onRemoveFilter(filter.key)}
                                        className="remove-filter-btn"
                                        title={`Remove ${filter.displayKey} filter`}
                                        disabled={loading}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="filter-actions-container">
                    <div className="filter-actions">
                        <button
                            onClick={onClearFilters}
                            className="clear-filters-btn"
                            disabled={!hasAppliedFilters || loading}
                            title="Clear all filters"
                        >
                            Clear All
                        </button>
                        <button
                            onClick={onApplyFilters}
                            className="apply-filters-btn"
                            disabled={loading}
                            title="Apply selected filters"
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FiltersSection;